import React, { useMemo, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useOutletContext } from 'react-router-dom';
import HistogramChart from './components/HistogramChart';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const FONT = "'CPF Imm Sook', sans-serif";

const normalPdf = (x, mu, sigma) =>
  (1 / (sigma * Math.sqrt(2 * Math.PI))) *
  Math.exp(-0.5 * ((x - mu) / sigma) ** 2);

/**
 * Build histogram data with per-bar color coding:
 *  – bins inside [lsl, usl] → blue (#5b9bd5)
 *  – bins outside spec limits → pink/red (#f4434699)
 */
const buildColoredHistData = (meanX, sigma, n, xMin, xMax, lsl, usl) => {
  const data = [];
  for (let x = xMin; x <= xMax; x++) {
    const count = Math.max(0, Math.round(n * normalPdf(x, meanX, sigma)));
    const inSpec = x >= lsl && x <= usl;
    data.push({ x, y: count, fillColor: inSpec ? '#5b9bd5' : '#f4434699' });
  }
  return data;
};

/** Determine status-circle color from Cpk when backend alert color is unavailable */
// const cpkColor = (cpk) => {
//   if (cpk >= 1.33) return '#4caf50';
//   if (cpk >= 1.0)  return '#2196f3';
//   if (cpk >= 0.67) return '#ffc107';
//   return '#f44336';
// };
const cpkColor = (cpk) => {
  if (cpk < 0.6) return '#f44336'; // red – capable
  if (cpk >= 0.6 && cpk <= 0.99) return '#ffc107'; // amber – marginal
  return '#4caf50';
};

// ─── Reusable small card ──────────────────────────────────────────────────────
const SectionCard = ({ children, sx = {} }) => (
  <Box
    sx={{
      bgcolor: '#0d0d0d',
      border: '1px solid #1e1e1e',
      borderRadius: '8px',
      p: '12px 16px',
      ...sx,
    }}
  >
    {children}
  </Box>
);

// ─── Mock defaults (matching page1 values) ────────────────────────────────────
const DEFAULT_MEAN = 706.67;
const DEFAULT_SIGMA = 1.83;
const DEFAULT_N = 88;
const DEFAULT_LSL = 700;
const DEFAULT_USL = 720;

const DEFAULT_HIST = buildColoredHistData(
  DEFAULT_MEAN, DEFAULT_SIGMA, DEFAULT_N,
  DEFAULT_LSL - 4, DEFAULT_USL + 4,
  DEFAULT_LSL, DEFAULT_USL,
);

const buildSmoothGaussianCurve = (
  points = [],
  mean,
  step = 0.1
) => {
  const validPoints = points
    .map((point) => ({
      x: Number(point?.x),
      y: Number(point?.y),
    }))
    .filter(
      (point) =>
        Number.isFinite(point.x) &&
        Number.isFinite(point.y) &&
        point.y >= 0
    )
    .sort((a, b) => a.x - b.x);

  if (validPoints.length < 2 || !Number.isFinite(Number(mean))) {
    return validPoints;
  }

  const mu = Number(mean);
  const totalY = validPoints.reduce(
    (sum, point) => sum + point.y,
    0
  );

  if (totalY <= 0) {
    return validPoints;
  }

  // ประมาณค่า Sigma จากจุด Curve ที่ Backend ส่งมา
  const variance =
    validPoints.reduce(
      (sum, point) =>
        sum + point.y * ((point.x - mu) ** 2),
      0
    ) / totalY;

  const sigma = Math.sqrt(variance);

  if (!Number.isFinite(sigma) || sigma <= 0) {
    return validPoints;
  }

  // หาความสูงของ Curve ที่เหมาะกับข้อมูลต้นทาง
  const gaussianValues = validPoints.map((point) =>
    Math.exp(
      -0.5 * ((point.x - mu) / sigma) ** 2
    )
  );

  const numerator = validPoints.reduce(
    (sum, point, index) =>
      sum + point.y * gaussianValues[index],
    0
  );

  const denominator = gaussianValues.reduce(
    (sum, value) => sum + value ** 2,
    0
  );

  const amplitude =
    denominator > 0
      ? numerator / denominator
      : Math.max(...validPoints.map((point) => point.y));

  const xMin = validPoints[0].x;
  const xMax = validPoints[validPoints.length - 1].x;
  const smoothPoints = [];

  for (let x = xMin; x <= xMax + step / 2; x += step) {
    smoothPoints.push({
      x: Number(x.toFixed(2)),
      y:
        amplitude *
        Math.exp(
          -0.5 * ((x - mu) / sigma) ** 2
        ),
    });
  }

  return smoothPoints;
};

// ─── Main page component ──────────────────────────────────────────────────────
const SigmaTrackGiveawayPage2 = () => {
  const {
    lsl = 0, usl = 0, processCapChart = [],
    fsData, rawWeights = [],
    avgWeight = 0, sigma5 = 0, totalPack: fgBag = 0, cpk5 = null, ppk5 = null,
    timeRange = '--:-- - --:--',
    alertColor = null,
    suggestionInline = 'กระบวนการยังคุมได้ แต่มีความผันผวน ให้เฝ้าระวังน้ำหนัก',
    showSuggestionInline = true,
    suggestionWeb = 'กระบวนการยังคุมได้ แต่มีความผันผวน ให้เฝ้าระวังน้ำหนัก',
    showSuggestionWeb = true,
  } = useOutletContext() ?? {};
  const cpk = cpk5;
  const ppk = ppk5;

  const cpkLabel = cpk >= 1.33 ? 'Excellent' : cpk >= 1.0 ? 'Capable' : cpk >= 0.67 ? 'Marginal' : 'Poor';

  // ── Histogram — colored bins from current 5-min bucket only ─────────────
  // const histData = useMemo(() => {
  //   if (!fsData) return [];
  //   const sigmaH = sigma5 > 0 ? sigma5 : (fsData?.SIGMA_5 ?? 0);
  //   const meanH = avgWeight > 0 ? avgWeight : (fsData?.X_I_5 ?? 0);
  //   const xMin = Math.floor(lsl || 0) - 4;
  //   const xMax = Math.ceil(usl || 0) + 4;

  //   if (sigmaH <= 0 || !lsl || !usl) return [];

  //   if (!rawWeights.length) {
  //     return [];
  //   }
  //   let bucketWeights = rawWeights;
  //   if (fsData?.BUCKET_TIME) {
  //     const bucketStart = new Date(fsData.BUCKET_TIME).getTime() / 1000;
  //     bucketWeights = rawWeights.filter(({ t }) => t >= bucketStart && t < bucketStart + 300);
  //   }

  //   if (!bucketWeights.length) {
  //     return buildColoredHistData(meanH, sigmaH, DEFAULT_N, xMin, xMax, lsl, usl);
  //   }

  //   const binCounts = {};
  //   bucketWeights.forEach(({ w }) => { const b = Math.round(w); binCounts[b] = (binCounts[b] || 0) + 1; });
  //   const data = [];
  //   for (let x = xMin; x <= xMax; x++) {
  //     data.push({ x, y: binCounts[x] || 0, fillColor: (x >= lsl && x <= usl) ? '#5b9bd5' : '#f4434699' });
  //   }
  //   return data;
  // }, [rawWeights, lsl, usl, fsData]);

  const processCapChartData = useMemo(() => {
    if (!Array.isArray(processCapChart) || processCapChart.length === 0) {
      return {
        histData: [],
        withinCurve: [],
        overallCurve: [],
        chartLsl: null,
        chartUsl: null,
        chartAvg: null,
        chartXMin: undefined,
        chartXMax: undefined,
      };
    }

    // กรองเฉพาะแถวที่มี bin_center ถูกต้อง และเรียงจากน้อยไปมาก
    const validRows = processCapChart
      .filter((row) => Number.isFinite(Number(row?.bin_center)))
      .sort(
        (a, b) =>
          Number(a.bin_center) - Number(b.bin_center)
      );

    if (validRows.length === 0) {
      return {
        histData: [],
        withinCurve: [],
        overallCurve: [],
        chartLsl: null,
        chartUsl: null,
        chartAvg: null,
        chartXMin: undefined,
        chartXMax: undefined,
      };
    }

    const firstRow = validRows[0];

    const chartLsl = Number(firstRow.lsl);
    const chartUsl = Number(firstRow.usl);
    const chartAvg = Number(firstRow.avg);

    // Histogram: bin_center เป็นแกน X และ count เป็นแกน Y
    const histData = validRows.map((row) => {
      const x = Number(row.bin_center);
      const count = Number(row.count);

      return {
        x,
        y: Number.isFinite(count) ? count : 0,
        fillColor:
          Number.isFinite(chartLsl) &&
            Number.isFinite(chartUsl) &&
            x >= chartLsl &&
            x <= chartUsl
            ? '#5b9bd5'
            : '#f4434699',
      };
    });

    // เส้นทึบ: Normal curve ระยะสั้น
    const withinCurve = validRows
      .filter(
        (row) =>
          Number.isFinite(Number(row.bin_center)) &&
          Number.isFinite(Number(row.y_within))
      )
      .map((row) => ({
        x: Number(row.bin_center),
        y: Number(row.y_within),
      }));

    // เส้นประ: Normal curve ระยะยาว
    const overallCurve = validRows
      .filter(
        (row) =>
          Number.isFinite(Number(row.bin_center)) &&
          Number.isFinite(Number(row.y_overall))
      )
      .map((row) => ({
        x: Number(row.bin_center),
        y: Number(row.y_overall),
      }));

    const lowEdges = validRows
      .map((row) => Number(row.low_edge))
      .filter(Number.isFinite);

    const binCenters = validRows
      .map((row) => Number(row.bin_center))
      .filter(Number.isFinite);

    // หาความกว้างของแต่ละ Bin
    const binWidth =
      binCenters.length > 1
        ? Math.abs(binCenters[1] - binCenters[0])
        : 1;

    // const minimumLowEdge =
    //   lowEdges.length > 0
    //     ? Math.min(...lowEdges)
    //     : Math.min(...binCenters) - binWidth / 2;

    // const maximumBinEdge =
    //   Math.max(...binCenters) + binWidth / 2;
    // ขอบจริงของ Histogram
    const histogramXMin =
      lowEdges.length > 0
        ? Math.min(...lowEdges)
        : Math.min(...binCenters) - binWidth / 2;

    const histogramXMax =
      Math.max(...binCenters) + binWidth / 2;

    // ให้แกน X ครอบคลุม Histogram, LSL และ USL
    const xValues = [
      histogramXMin,
      histogramXMax,
      chartLsl,
      chartUsl,
    ].filter(Number.isFinite);

    const xAxisPadding = binWidth * 0.5;

    const minimumLowEdge =
      Math.min(...xValues) - xAxisPadding;

    const maximumBinEdge =
      Math.max(...xValues) + xAxisPadding;

    return {
      histData,
      withinCurve,
      overallCurve,
      chartLsl: Number.isFinite(chartLsl) ? chartLsl : null,
      chartUsl: Number.isFinite(chartUsl) ? chartUsl : null,
      chartAvg: Number.isFinite(chartAvg) ? chartAvg : null,
      chartXMin: minimumLowEdge,
      chartXMax: maximumBinEdge,
    };
  }, [processCapChart]);

  const {
    histData,
    withinCurve,
    overallCurve,
    chartLsl,
    chartUsl,
    chartAvg,
    chartXMin,
    chartXMax,
  } = processCapChartData;

  // เพิ่มความละเอียดของ y_within ให้เป็น Gaussian Curve
  const smoothWithinCurve = useMemo(
    () =>
      buildSmoothGaussianCurve(
        withinCurve,
        chartAvg,
        0.1
      ),
    [withinCurve, chartAvg]
  );

  // เพิ่มความละเอียดของ y_overall ให้เป็น Gaussian Curve
  const smoothOverallCurve = useMemo(
    () =>
      buildSmoothGaussianCurve(
        overallCurve,
        chartAvg,
        0.1
      ),
    [overallCurve, chartAvg]
  );
  const NO_DATA_COLOR = '#757575';

  const hasHistogramData = useMemo(() => {
    return histData.some((point) => {
      const y = Number(point?.y);
      return Number.isFinite(y) && y > 0;
    });
  }, [histData]);

  // เลือกค่าที่แย่ที่สุดระหว่าง Cpk และ Ppk
  const worstCapabilityValue = useMemo(() => {
    const values = [cpk, ppk]
      .filter((value) => value !== null && value !== undefined && value !== '')
      .map(Number)
      .filter(Number.isFinite);

    return values.length ? Math.min(...values) : null;
  }, [cpk, ppk]);

  const worstColor = (...colors) => {
    const values = colors.map((color) => String(color || '').toUpperCase());

    if (values.some((color) => color === 'RED' || color === '#F44336')) return '#f44336';
    if (values.some((color) => color === 'YELLOW' || color === '#FFC107')) return '#ffc107';
    return '#4caf50';
  };

  const avgStatusColor =
    !hasHistogramData || worstCapabilityValue === null
      ? NO_DATA_COLOR
      : worstColor(alertColor, cpkColor(worstCapabilityValue));

  // const statusColor = alertColor ?? cpkColor(cpk ?? 0);
  const hasCpkData = hasHistogramData && cpk != null && cpk !== '' && Number.isFinite(Number(cpk));
  const statusColor = hasCpkData ? cpkColor(Number(cpk)) : NO_DATA_COLOR;

  const processStats = useMemo(() => {
    const sampleWeights = Array.isArray(rawWeights) && rawWeights.length
      ? rawWeights
        .filter(({ w }) => Number.isFinite(Number(w)))
        .map(({ w }) => Number(w))
      : Array.from({ length: 15 }, (_, i) => 700 + (i % 7) * 1.5 + ((i % 3) * 0.4));

    if (!sampleWeights.length) {
      return { xbar: 0, mrBar: 0, sigmaWithin: 0, sigmaOverall: 0, cpk: 0, ppk: 0, mrSeries: [] };
    }

    const xbar = sampleWeights.reduce((sum, v) => sum + v, 0) / sampleWeights.length;
    const mrSeries = [];
    for (let i = 1; i < sampleWeights.length; i += 1) {
      mrSeries.push(Math.abs(sampleWeights[i] - sampleWeights[i - 1]));
    }
    const mrBar = mrSeries.length ? mrSeries.reduce((sum, v) => sum + v, 0) / mrSeries.length : 0;
    const sigmaOverall = Math.sqrt(
      sampleWeights.reduce((sum, v) => sum + (v - xbar) ** 2, 0) / Math.max(sampleWeights.length - 1, 1)
    );
    const sigmaWithin = mrBar / 1.128 || 0;
    const cpk = sigmaWithin > 0 ? Math.min((usl - xbar) / (3 * sigmaWithin), (xbar - lsl) / (3 * sigmaWithin)) : 0;
    const ppk = sigmaOverall > 0 ? Math.min((usl - xbar) / (3 * sigmaOverall), (xbar - lsl) / (3 * sigmaOverall)) : 0;

    return {
      xbar,
      mrBar,
      sigmaWithin,
      sigmaOverall,
      cpk,
      ppk,
      mrSeries,
    };
  }, [rawWeights, lsl, usl]);

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const [showCalcDetail, setShowCalcDetail] = useState(false);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: 'calc(100vh - 100px)' },
        minHeight: { xs: '100vh', md: 'unset' },
        bgcolor: '#0a0a0a',
        color: '#fff',
        fontFamily: FONT,
        overflow: { xs: 'auto', md: 'hidden' },
        userSelect: 'none',
        p: 1.5,
        gap: 1,
      }}
    >
      {/* ── PAGE HEADER ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: { xs: 22, sm: 28, md: 36 },
            fontWeight: 'bold',
            color: '#fff',
            lineHeight: 1,
          }}
        >
          Process Capability (5 min)
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: { xs: 16, md: 28 },
            color: '#ccc',
            letterSpacing: 2,
          }}
        >
          {timeRange}
        </Typography>
      </Box>

      {/* ── BODY ── */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, gap: 1, overflow: { xs: 'visible', md: 'hidden' } }}>

        {/* ── HISTOGRAM (left, large) ── */}
        <Box
          sx={{
            flex: 1,
            bgcolor: '#111',
            border: '1px solid #222',
            borderRadius: '8px',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'stretch',
          }}
        >
          {/* <HistogramChart
            histData={histData}
            lsl={lsl}
            usl={usl}
            avg={avgWeight}
            lslLabel={`LSL ${lsl}`}
            uslLabel={`USL ${usl}`}
            avgLabel="Avg."
            labelBgColor="#1a1a1a"
            labelTextColor="#ff6b6b"
            labelFontSize="13px"
            xMin={lsl - 4}
            xMax={usl + 4}
            height={isSmall ? 260 : 390}
          /> */}
          <HistogramChart
            histData={histData}
            // y_within = เส้นทึบสีแดงแบบ Gaussian
            actualCurve={smoothWithinCurve}
            // y_overall = เส้นประสีเทาแบบ Gaussian
            normalCurve={smoothOverallCurve}

            lsl={chartLsl}
            usl={chartUsl}
            avg={chartAvg}

            lslLabel={`LSL ${chartLsl ?? '-'}`}
            uslLabel={`USL ${chartUsl ?? '-'}`}
            avgLabel={`Avg. ${chartAvg ?? '-'}`}

            labelBgColor="#1a1a1a"
            labelTextColor="#ff6b6b"
            labelFontSize="13px"

            xMin={chartXMin}
            xMax={chartXMax}
            width='1800%'
            height={isSmall ? 260 : 390}
          />
        </Box>

        {/* ── RIGHT PANEL ── */}
        <Box
          sx={{
            width: { xs: '100%', md: 220 },
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <SectionCard sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderLeft: `3px solid ${avgStatusColor}` }}>
            <Box>
              <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#666', letterSpacing: 1.5 }}></Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 22, fontWeight: 'bold', color: /*avgStatusColor*/'#fff', mt: 0.3 }}>
                {/* {cpkLabel} */'Status'}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                bgcolor: avgStatusColor,
                boxShadow: hasHistogramData
                  ? `0 0 16px ${avgStatusColor}66`
                  : 'none',
                flexShrink: 0,
              }}
            />
          </SectionCard>

          <SectionCard sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 170 }}>
            <Box>
              <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#666', letterSpacing: 1.5 }}>AVG ACT. (g/bag)</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 52, fontWeight: 'bold', color: '#fff', lineHeight: 1.08, mt: 0.3 }}>
                {avgWeight > 0 ? avgWeight.toFixed(1) : '--'}
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#555' }}>g / bag</Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7, mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #1e1e1e', pt: 1 }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#666' }}>LSL</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#aaa', fontWeight: 'bold' }}>{lsl}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#666' }}>USL</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#aaa', fontWeight: 'bold' }}>{usl}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#666' }}>σ (5min)</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#aaa', fontWeight: 'bold' }}>{sigma5.toFixed(2)}</Typography>
              </Box>
            </Box>
          </SectionCard>

          <SectionCard sx={{ position: 'relative', minHeight: 110 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#888', mb: 0.5 }}>FG (bag)</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 40, fontWeight: 'bold', color: '#fff', lineHeight: 1 }}>
                  {fgBag}
                </Typography>
              </Box>

              <Box sx={{ position: 'relative', textAlign: 'right' }}>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowCalcDetail((prev) => !prev)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setShowCalcDetail((prev) => !prev);
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    right: -8,
                    top: -8,
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    border: `1px solid ${statusColor}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: statusColor,
                    fontFamily: FONT,
                    fontSize: 11,
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    userSelect: 'none',
                    lineHeight: 1,
                    background: '#111',
                  }}
                >
                  i
                </Box>
                <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#888', mb: 0.5 }}>Cpk</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 40, fontWeight: 'bold', color: statusColor, lineHeight: 1 }}>
                  {hasCpkData ? Number(cpk).toFixed(2) : '-'}
                </Typography>
              </Box>
            </Box>
          </SectionCard>

        </Box>
      </Box>

      {/* {showSuggestionWeb && !!suggestionWeb && (
        <SectionCard
          sx={{
            flexShrink: 0,
            border: `1px solid ${statusColor}66`,
            bgcolor: '#0f0c00',
            minHeight: 52,
          }}
        >
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#e0c060' }}>
            {suggestionWeb}
          </Typography>
        </SectionCard>
      )} */}
      {showSuggestionWeb &&
        Array.isArray(suggestionWeb) &&
        suggestionWeb.length > 0 && (
          <SectionCard
            sx={{
              flexShrink: 0,
              border: `1px solid ${statusColor}66`,
              bgcolor: '#0f0c00',
              minHeight: 52,
            }}
          >
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 3,
              }}
            >
              <Typography
                component="li"
                sx={{
                  fontFamily: FONT,
                  fontSize: 26,
                  color: '#e0c060',
                  lineHeight: 1,
                }}
              >
                {suggestionWeb[0]}
              </Typography>
            </Box>
          </SectionCard>
        )}

      {showCalcDetail && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, flexShrink: 0 }}>
          <Box sx={{ bgcolor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '6px', p: 1 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#aaa', mb: 0.75 }}>Cpk formula</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 0.5, fontFamily: FONT, fontSize: 11, color: '#ddd' }}>
              <Box>Mean (Xbar)</Box><Box sx={{ textAlign: 'right' }}>{processStats.xbar.toFixed(2)}</Box>
              <Box>σ within</Box><Box sx={{ textAlign: 'right' }}>{processStats.sigmaWithin.toFixed(2)}</Box>
              <Box>LSL</Box><Box sx={{ textAlign: 'right' }}>{lsl}</Box>
              <Box>USL</Box><Box sx={{ textAlign: 'right' }}>{usl}</Box>
              <Box>(USL - Xbar)/(3σw)</Box><Box sx={{ textAlign: 'right' }}>{((usl - processStats.xbar) / (3 * Math.max(processStats.sigmaWithin, 1e-9))).toFixed(2)}</Box>
              <Box>(Xbar - LSL)/(3σw)</Box><Box sx={{ textAlign: 'right' }}>{((processStats.xbar - lsl) / (3 * Math.max(processStats.sigmaWithin, 1e-9))).toFixed(2)}</Box>
              <Box>Cpk</Box><Box sx={{ textAlign: 'right', color: statusColor, fontWeight: 'bold' }}>{processStats.cpk.toFixed(2)}</Box>
            </Box>
          </Box>
          <Box sx={{ bgcolor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '6px', p: 1 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#aaa', mb: 0.75 }}>Ppk formula</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 0.5, fontFamily: FONT, fontSize: 11, color: '#ddd' }}>
              <Box>σ overall</Box><Box sx={{ textAlign: 'right' }}>{processStats.sigmaOverall.toFixed(2)}</Box>
              <Box>(USL - Xbar)/(3σo)</Box><Box sx={{ textAlign: 'right' }}>{((usl - processStats.xbar) / (3 * Math.max(processStats.sigmaOverall, 1e-9))).toFixed(2)}</Box>
              <Box>(Xbar - LSL)/(3σo)</Box><Box sx={{ textAlign: 'right' }}>{((processStats.xbar - lsl) / (3 * Math.max(processStats.sigmaOverall, 1e-9))).toFixed(2)}</Box>
              <Box>Ppk</Box><Box sx={{ textAlign: 'right', color: statusColor, fontWeight: 'bold' }}>{processStats.ppk.toFixed(2)}</Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SigmaTrackGiveawayPage2;
